import React from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Zap, LayoutDashboard, Mail, CalendarCheck, Plug, LogOut, ExternalLink, ShieldCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function DashboardLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050818] flex items-center justify-center text-slate-300">
        <div className="animate-pulse">Loading dashboard...</div>
      </div>
    );
  }

  if (!user) {
    nav("/dashboard/login", { replace: true, state: { from: loc.pathname } });
    return null;
  }

  if (user.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#050818] flex items-center justify-center px-6 text-center">
        <div className="glass rounded-2xl p-10 max-w-md">
          <ShieldCheck className="w-10 h-10 text-cyan-300 mx-auto mb-3" />
          <h2 className="font-display text-2xl font-bold text-white">Admin access required</h2>
          <p className="mt-2 text-slate-400">Your account (<span className="text-cyan-300">{user.email}</span>) does not have admin privileges.</p>
          <button onClick={() => { logout(); nav("/dashboard/login"); }} className="mt-6 btn-primary rounded-xl h-11 px-5 font-semibold border-0">Sign out</button>
        </div>
      </div>
    );
  }

  const navItems = [
    { to: "/dashboard", end: true, icon: LayoutDashboard, label: "Overview" },
    { to: "/dashboard/contacts", icon: Mail, label: "Contacts" },
    { to: "/dashboard/bookings", icon: CalendarCheck, label: "Bookings" },
    { to: "/dashboard/integrations", icon: Plug, label: "Integrations" },
  ];

  return (
    <div className="min-h-screen bg-[#050818] text-slate-200">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 border-r border-white/5 bg-[#060a1c] flex flex-col">
        <Link to="/" className="flex items-center gap-3 px-5 h-20 border-b border-white/5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-400 flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-white text-base">FortressPoint</div>
            <div className="text-[10px] tracking-widest uppercase text-cyan-300/80">Admin</div>
          </div>
        </Link>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((n) => {
            const I = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition ${
                    isActive
                      ? "bg-gradient-to-r from-sky-500/20 to-cyan-400/10 border border-cyan-400/25 text-white"
                      : "text-slate-300 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <I className="w-4 h-4 text-cyan-300" /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/5">
          <Link to="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400 hover:text-cyan-300">
            <ExternalLink className="w-3.5 h-3.5" /> View public site
          </Link>
          <div className="px-3 pt-3 pb-2">
            <div className="text-xs text-slate-500">Signed in as</div>
            <div className="text-sm text-white truncate">{user.name}</div>
            <div className="text-[11px] text-slate-400 truncate">{user.email}</div>
          </div>
          <button onClick={() => { logout(); nav("/dashboard/login"); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-lg">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="pl-64">
        <div className="max-w-6xl mx-auto p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
